import React from 'react';
import { useTranslation } from 'react-i18next';
import { AvatarFrame, FrameType } from './AvatarFrame';
import { TitleBadge, TitleType } from './TitleBadge';

const FRAMES: FrameType[] = ['none', 'bronze', 'silver', 'gold', 'platinum', 'diamond'];
const TITLES: TitleType[] = ['beginner', 'apprentice', 'scholar', 'expert', 'master', 'legend'];

const FRAME_LEVELS: Record<FrameType, number> = {
  none: 1,
  bronze: 5,
  silver: 10,
  gold: 15,
  platinum: 20,
  diamond: 25,
};

const TITLE_LEVELS: Record<TitleType, number> = {
  beginner: 1,
  apprentice: 5,
  scholar: 10,
  expert: 15,
  master: 20,
  legend: 25,
};

interface ProgressionShowcaseProps {
  avatarUrl?: string;
}

export const ProgressionShowcase: React.FC<ProgressionShowcaseProps> = ({
  avatarUrl = 'https://api.dicebear.com/7.x/avataaars/svg?seed=demo',
}) => {
  const { t } = useTranslation('progression');

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-bold mb-8 text-center">{t('showcase.pageTitle')}</h1>

      {/* Avatar Frames Section */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">{t('showcase.avatarFrames')}</h2>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {FRAMES.map((frame) => (
              <div key={frame} className="flex flex-col items-center">
                <AvatarFrame frame={frame} avatarUrl={avatarUrl} size={80} />
                <div className="mt-3 text-center">
                  <p className="font-medium text-gray-800">{t(`frames.${frame}`)}</p>
                  <p className="text-sm text-gray-500">Lv.{FRAME_LEVELS[frame]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Title Badges Section */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">{t('showcase.titleBadges')}</h2>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {TITLES.map((title) => (
              <div key={title} className="flex flex-col items-center">
                <TitleBadge title={title} size={60} />
                <div className="mt-3 text-center">
                  <p className="font-medium text-gray-800">{t(`titles.${title}`)}</p>
                  <p className="text-sm text-gray-500">Lv.{TITLE_LEVELS[title]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Size Variations */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">{t('showcase.sizeVariations')}</h2>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-end gap-6 flex-wrap">
            {[40, 60, 80, 100, 120].map((size) => (
              <div key={size} className="flex flex-col items-center">
                <AvatarFrame frame="gold" avatarUrl={avatarUrl} size={size} />
                <p className="mt-2 text-sm text-gray-500">{size}px</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Combined Preview */}
      <section>
        <h2 className="text-xl font-semibold mb-4 text-gray-700">{t('showcase.combinedPreview')}</h2>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { frame: 'bronze' as FrameType, title: 'apprentice' as TitleType, level: 5 },
              { frame: 'silver' as FrameType, title: 'scholar' as TitleType, level: 10 },
              { frame: 'gold' as FrameType, title: 'expert' as TitleType, level: 15 },
              { frame: 'platinum' as FrameType, title: 'master' as TitleType, level: 20 },
              { frame: 'diamond' as FrameType, title: 'legend' as TitleType, level: 25 },
            ].map(({ frame, title, level }) => (
              <div
                key={level}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg"
              >
                <AvatarFrame frame={frame} avatarUrl={avatarUrl} size={70} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <TitleBadge title={title} size={32} />
                    <span className="font-medium text-gray-800">
                      {t(`titles.${title}`)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Level {level} · {t(`frames.${frame}`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgressionShowcase;
